/**
 * The airports admin page's reads and its save (SA-1054 §5.2). Its own file so
 * `useAirports.ts` stays as SA-1031 shipped it. Every key sits under
 * `airportsKey`, so the save's one invalidation refreshes this browser's
 * resolve, search and house reads together; other viewers' in-memory caches
 * refresh on their next page load.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { airportsKey, SEARCH_DEBOUNCE_MS, SEARCH_MIN, useAirportsAvailable } from '@/hooks/useAirports'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { listHouseAirports, saveAirportHouse, searchAirports, type AirportHousePatch } from '@/services/airports'

export const ADMIN_SEARCH_LIMIT = 20

export function useHouseAirports() {
  // SA-1366: the kit's route; strict on the loaded `aviation` flag, as every airports read.
  const available = useAirportsAvailable()
  return useQuery({ queryKey: [...airportsKey, 'house'], enabled: available, queryFn: listHouseAirports, retry: false })
}

export function useAirportAdminSearch(q: string) {
  const text = useDebouncedValue(q.trim(), SEARCH_DEBOUNCE_MS)
  const available = useAirportsAvailable()
  const query = useQuery({
    queryKey: [...airportsKey, 'admin-search', text],
    enabled: available && text.length >= SEARCH_MIN,
    retry: false,
    queryFn: () => searchAirports(text, { limit: ADMIN_SEARCH_LIMIT }),
  })
  // `asked` is the debounced text the result belongs to: a mismatch is "still searching".
  return { ...query, asked: text }
}

export function useSaveAirportHouse() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ident, ...patch }: { ident: string } & AirportHousePatch) => saveAirportHouse(ident, patch),
    // Returned, so mutateAsync resolves once the refreshed reads are back.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: airportsKey }),
  })
}
